import { Vertical } from "@/components/standard/BFLayout"
import {
  FieldWithLabel,
  TextFieldWithLabel,
} from "@/components/forms/FieldWithLabel"
import { Body, H5, Small, Tiny } from "@/components/standard/BFText"
import { useRef, useState } from "react"
import { AddressSelector } from "@/screens/loans/components/AddressSelector"
import { ScrollView, View } from "react-native"
import { BFButton } from "@/components/buttons/CommonButtons"
import { Masks } from "react-native-mask-input"
import { useCreditPull } from "@/queries/loan"
import { showErrorToast } from "@/components/toasts/AppToast"

export interface CreditPollState {
  loanId: string
  firstName: string
  lastName: string
  phoneNumber: string
  placeId: string
  address2: string
  dateOfBirth: string
}

export const SoftCreditPollScreen = ({
  defaults,
  onSubmit,
}: {
  defaults: Partial<CreditPollState>
  onSubmit: () => void
}) => {
  const [address, setAddress] = useState<string | undefined>(undefined)

  const softCreditPollState = useRef<Partial<CreditPollState>>(defaults || {})
  const [isValid, setIsValid] = useState(false)

  const createCreditPull = useCreditPull()

  function setAndCheckValid(
    field: keyof CreditPollState,
    value: string | undefined
  ) {
    const state = softCreditPollState.current
    if (field) {
      state[field] = value
    }
    const valid =
      !!state.firstName &&
      !!state.lastName &&
      !!state.phoneNumber &&
      !!state.dateOfBirth &&
      !!state.placeId

    console.log(state, valid)

    setIsValid(valid)
  }

  function onSetAddress(
    address: string | undefined,
    placeId: string | undefined
  ) {
    setAddress(address)
    setAndCheckValid("placeId", placeId)
  }

  if (!defaults.loanId) {
    return (
      <Vertical className={"flex-1 justify-center items-center p-10"}>
        <H5>Missing or Invalid LoanId</H5>
      </Vertical>
    )
  }

  function onCreditPull() {
    createCreditPull.mutate(softCreditPollState.current as CreditPollState, {
      onSuccess: onSubmit,
      onError: (err) => {
        showErrorToast({
          title: "Failed to create Credit Pull",
          message: err.message || String(err),
        })
      },
    })
  }

  return (
    <Vertical className={"flex-1"}>
      {/* <View className={"flex-1"}> */}
      <ScrollView>
        <Vertical className={"gap-5"}>
          <Body>
            Let&apos;s quickly check your loan eligibility with a soft credit
            check. (Don&apos;t worry, this won&apos;t affect your credit score!)
          </Body>
          <Small>
            If applying as an entity or with multiple borrowers, provide only
            your own information.
          </Small>

          <TextFieldWithLabel
            label={"First Name"}
            initialValue={defaults?.firstName}
            required={true}
            onValueChange={(value) => {
              setAndCheckValid("firstName", value)
            }}
          />

          <TextFieldWithLabel
            label={"Last Name"}
            initialValue={defaults?.lastName}
            required={true}
            onValueChange={(value) => {
              setAndCheckValid("lastName", value)
            }}
          />
          <TextFieldWithLabel
            label={"Phone Number"}
            initialValue={defaults?.phoneNumber}
            required={true}
            validator={(value) =>
              value.length !== 10 ? "Invalid phone number" : undefined
            }
            onValueChange={(value, error) => {
              setAndCheckValid("phoneNumber", error ? undefined : value)
            }}
            mask={Masks.USA_PHONE}
            textFieldProps={{
              placeholder: "(___) ___-____",
              keyboardType: "numeric",
            }}
          />
          <TextFieldWithLabel
            label={"Date of Birth"}
            required={true}
            mask={Masks.DATE_MMDDYYYY}
            onValueChange={(value, error) => {
              setAndCheckValid("dateOfBirth", error ? undefined : value)
            }}
            validator={(value) =>
              value.length !== 8 ? "Invalid date" : undefined
            }
            textFieldProps={{
              placeholder: "MM/DD/YYYY",
              keyboardType: "numeric",
            }}
          />
          <FieldWithLabel
            label={"Primary Home Address"}
            description={"Your personal address (not a business address)"}
            required={true}
          >
            <AddressSelector onSelect={onSetAddress} />
          </FieldWithLabel>
          {address && address !== "" && (
            <TextFieldWithLabel
              label={"Apt., suite, etc"}
              required={false}
              onValueChange={(value) => {
                setAndCheckValid("address2", value)
              }}
              textFieldProps={{
                placeholder: "Unit # / Apt #",
              }}
            />
          )}
        </Vertical>
        <View className={"mb-8 mt-2 gap-1"}>
          <Tiny className={"px-0 pt-5 pb-3"}>
            By submitting this form, you acknowledge that everything is true and
            correct to the best of your knowledge. If pre-approved, you
            authorize us on your own and not as representative of any entity to
            do an initial &quot;Soft Pull&quot; of credit. Further, you
            acknowledge that a hard credit pull, which does impact credit, will
            be required before a Backflip loan is finally approved.
          </Tiny>
          <BFButton
            disabled={!isValid}
            title={"Submit"}
            testID={"submit-button"}
            onPress={onCreditPull}
          />
        </View>
      </ScrollView>
      {/* </View> */}
    </Vertical>
  )
}
